import {useState, useRef, useEffect, useCallback} from "react";
import {
    Box, Stack, Typography, Card, CardContent, TextField, Select, MenuItem,
    Button, IconButton, Chip, InputAdornment,
} from "@mui/material";
import {
    IconSearch, IconZoomIn, IconZoomOut, IconRotateClockwise2,
    IconFilter, IconAlertCircle,
} from "@tabler/icons-react";
import {KnowledgeDatabaseRepository} from "../../../../repositories/KnowledgeDatabaseRepository";

const cardSx = {borderRadius: 2, border: "1px solid", borderColor: "divider", boxShadow: "none"};

function buildGraphNodes(topic, label, rawNodes) {
    const typeColors = {
        entity: "#6366f1",
        relation: "#f97316",
        chunk: "#22c55e",
        topic: topic.color,
    };

    const topicNode = {
        id: "__graph-center__",
        label,
        type: "topic",
        properties: {
            topicId: topic.id,
            topicName: topic.name,
            description: topic.description,
        },
        x: 400, y: 300, vx: 0, vy: 0,
        color: topic.color,
        size: 16,
    };

    const nodes = rawNodes.map((node, index) => {
        const angle = rawNodes.length > 0 ? (index / rawNodes.length) * Math.PI * 2 : 0;
        const radius = 120 + Math.random() * 80;

        return {
            id: node.id,
            label: node.label,
            type: node.type,
            properties: node.properties,
            x: 400 + Math.cos(angle) * radius + (Math.random() - 0.5) * 40,
            y: 300 + Math.sin(angle) * radius + (Math.random() - 0.5) * 40,
            vx: 0, vy: 0,
            color: typeColors[node.type] ?? "#6366f1",
            size: 6 + Math.random() * 6,
        };
    });

    return [topicNode, ...nodes];
}

export const KnowledgeGraphView = ({topic}) => {
    const canvasRef = useRef(null);
    const [graph, setGraph] = useState({nodes: [], edges: []});
    const [isLoading, setIsLoading] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({x: 0, y: 0});
    const [selectedNode, setSelectedNode] = useState(null);
    const [nodeSearchTerm, setNodeSearchTerm] = useState("");
    const [labelQuery, setLabelQuery] = useState("");
    const [activeLabel, setActiveLabel] = useState("");
    const [labelSuggestions, setLabelSuggestions] = useState([]);
    const [popularLabels, setPopularLabels] = useState([]);
    const [filterType, setFilterType] = useState("all");
    const [hoveredNode, setHoveredNode] = useState(null);
    const [error, setError] = useState(null);
    const [maxDepth, setMaxDepth] = useState("2");
    const [maxNodes, setMaxNodes] = useState("100");
    const isDragging = useRef(false);
    const dragStart = useRef({x: 0, y: 0});
    const dragNode = useRef(null);

    const getNodeAt = useCallback((mx, my) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const x = (mx - rect.left - offset.x) / zoom;
        const y = (my - rect.top - offset.y) / zoom;

        for (const node of graph.nodes) {
            const dx = node.x - x;
            const dy = node.y - y;
            if (Math.sqrt(dx * dx + dy * dy) < node.size + 4) {
                return node;
            }
        }
        return null;
    }, [graph.nodes, zoom, offset]);

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const dpr = window.devicePixelRatio || 1;
        canvas.width = canvas.clientWidth * dpr;
        canvas.height = canvas.clientHeight * dpr;
        ctx.scale(dpr, dpr);

        ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
        ctx.save();
        ctx.translate(offset.x, offset.y);
        ctx.scale(zoom, zoom);

        const filteredNodes = filterType === "all" ? graph.nodes : graph.nodes.filter(node => node.type === filterType);
        const filteredIds = new Set(filteredNodes.map(node => node.id));

        for (const edge of graph.edges) {
            if (!filteredIds.has(edge.source) || !filteredIds.has(edge.target)) continue;
            const source = graph.nodes.find(node => node.id === edge.source);
            const target = graph.nodes.find(node => node.id === edge.target);
            if (!source || !target) continue;

            ctx.beginPath();
            ctx.moveTo(source.x, source.y);
            ctx.lineTo(target.x, target.y);
            ctx.strokeStyle = selectedNode && (selectedNode.id === source.id || selectedNode.id === target.id)
                ? "rgba(99,102,241,0.6)" : "rgba(0,0,0,0.08)";
            ctx.lineWidth = selectedNode && (selectedNode.id === source.id || selectedNode.id === target.id) ? 1.5 : 0.5;
            ctx.stroke();

            if (zoom > 0.8) {
                const midX = (source.x + target.x) / 2;
                const midY = (source.y + target.y) / 2;
                ctx.fillStyle = "rgba(0,0,0,0.3)";
                ctx.font = "8px sans-serif";
                ctx.textAlign = "center";
                ctx.fillText(edge.label, midX, midY - 3);
            }
        }

        for (const node of filteredNodes) {
            const isHighlighted = nodeSearchTerm.length > 0 && node.label.toLowerCase().includes(nodeSearchTerm.toLowerCase());
            const isSelected = selectedNode?.id === node.id;
            const isHovered = hoveredNode?.id === node.id;

            ctx.beginPath();
            ctx.arc(node.x, node.y, node.size, 0, Math.PI * 2);

            if (isSelected || isHighlighted) {
                ctx.shadowColor = node.color;
                ctx.shadowBlur = 12;
            }

            ctx.fillStyle = isHovered ? node.color : nodeSearchTerm && !isHighlighted ? `${node.color}40` : node.color;
            ctx.fill();
            ctx.shadowBlur = 0;

            if (isSelected) {
                ctx.strokeStyle = "#fff";
                ctx.lineWidth = 2;
                ctx.stroke();
            }

            if (zoom > 0.6 || isSelected || isHighlighted || isHovered || node.type === "topic") {
                ctx.fillStyle = nodeSearchTerm && !isHighlighted ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.7)";
                ctx.font = `${node.type === "topic" ? "bold 11px" : "10px"} sans-serif`;
                ctx.textAlign = "center";
                ctx.fillText(node.label, node.x, node.y + node.size + 12);
            }
        }

        ctx.restore();
    }, [filterType, graph, hoveredNode, nodeSearchTerm, offset, selectedNode, zoom]);

    const resetView = useCallback(() => {
        setZoom(1);
        setOffset({x: 0, y: 0});
        setSelectedNode(null);
        setNodeSearchTerm("");
        setFilterType("all");
    }, []);

    const loadGraph = useCallback(async (label) => {
        const nextLabel = label.trim();
        if (!nextLabel) {
            setError("Choose a graph label first.");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await KnowledgeDatabaseRepository.fetch(topic.id, {
                label: nextLabel,
                maxDepth: Number(maxDepth),
                maxNodes: Number(maxNodes),
            });

            const nodes = buildGraphNodes(topic, nextLabel, response.nodes);
            const nodeIds = new Set(nodes.map(node => node.id));
            const edges = response.edges
                .filter(edge => nodeIds.has(edge.source) && nodeIds.has(edge.target))
                .map(edge => ({source: edge.source, target: edge.target, label: edge.label}));

            setActiveLabel(nextLabel);
            setGraph({nodes, edges});
            setSelectedNode(nodes[0] ?? null);
            setOffset({x: 0, y: 0});
            setZoom(1);
        } catch (err) {
            setGraph({nodes: [], edges: []});
            setSelectedNode(null);
            setError(err.message || "Knowledge graph operation failed");
        } finally {
            setIsLoading(false);
        }
    }, [maxDepth, maxNodes, topic]);

    useEffect(() => {
        draw();
    }, [draw]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const handleResize = () => draw();
        const observer = new ResizeObserver(handleResize);
        observer.observe(canvas);
        return () => observer.disconnect();
    }, [draw]);

    useEffect(() => {
        let cancelled = false;

        async function bootstrap() {
            setError(null);
            try {
                const popular = await KnowledgeDatabaseRepository.listPopularLabels(topic.id, 12);
                if (cancelled) return;

                setPopularLabels(popular);
                setLabelSuggestions(popular);

                const initialLabel = popular[0] ?? topic.name;
                setLabelQuery(initialLabel);
                if (initialLabel) {
                    void loadGraph(initialLabel);
                }
            } catch (err) {
                if (cancelled) return;
                setPopularLabels([]);
                setLabelSuggestions([]);
                setError(err.message || "Knowledge graph operation failed");
            }
        }

        void bootstrap();
        return () => {
            cancelled = true;
        };
    }, [loadGraph, topic.id, topic.name]);

    useEffect(() => {
        let cancelled = false;

        const timeoutId = window.setTimeout(() => {
            const q = labelQuery.trim();
            if (!q) {
                setLabelSuggestions(popularLabels);
                return;
            }

            KnowledgeDatabaseRepository.searchLabels(topic.id, q, 10)
                .then(labels => {
                    if (!cancelled) setLabelSuggestions(labels);
                })
                .catch(() => {
                    if (!cancelled) setLabelSuggestions([]);
                });
        }, 250);

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [labelQuery, popularLabels, topic.id]);

    const handleMouseDown = (e) => {
        const node = getNodeAt(e.clientX, e.clientY);
        if (node) {
            dragNode.current = node;
            setSelectedNode(node);
            return;
        }

        isDragging.current = true;
        dragStart.current = {x: e.clientX - offset.x, y: e.clientY - offset.y};
        setSelectedNode(null);
    };

    const handleMouseMove = (e) => {
        if (dragNode.current) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const x = (e.clientX - rect.left - offset.x) / zoom;
            const y = (e.clientY - rect.top - offset.y) / zoom;
            dragNode.current.x = x;
            dragNode.current.y = y;
            draw();
            return;
        }

        if (isDragging.current) {
            setOffset({x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y});
            return;
        }

        const node = getNodeAt(e.clientX, e.clientY);
        setHoveredNode(node);
        const canvas = canvasRef.current;
        if (canvas) canvas.style.cursor = node ? "pointer" : "grab";
    };

    const handleMouseUp = () => {
        isDragging.current = false;
        dragNode.current = null;
    };

    const handleDoubleClick = (e) => {
        const node = getNodeAt(e.clientX, e.clientY);
        if (!node) return;

        setSelectedNode(node);
        setLabelQuery(node.label);
        void loadGraph(node.label);
    };

    const handleWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setZoom(currentZoom => Math.max(0.2, Math.min(3, currentZoom * delta)));
    };

    if (!topic.server) {
        return (
            <Card sx={cardSx}>
                <CardContent sx={{py: 5, textAlign: "center"}}>
                    <IconAlertCircle size={36} color={"#9ca3af"} style={{marginBottom: 8}}/>
                    <Typography fontSize={14}>Knowledge graph requires a configured LightRAG server.</Typography>
                    <Typography fontSize={12} color={"text.secondary"} sx={{mt: 0.5}}>
                        Configure the topic server in Settings, then reload the graph tab.
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Stack gap={2}>

            <Stack direction={"row"} gap={1.25} flexWrap={"wrap"}>
                <TextField
                    size={"small"} sx={{
                    flex: 1,
                    minWidth: 220,
                    "& .MuiOutlinedInput-root": {
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "divider",
                            borderWidth: "1px",
                        },
                    },
                }}
                    value={labelQuery}

                    onChange={(e) => setLabelQuery(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            void loadGraph(labelQuery);
                        }
                    }}
                    placeholder={"Search graph labels..."}
                    InputProps={{
                        startAdornment: <InputAdornment position={"start"}><IconSearch size={14}/></InputAdornment>
                    }}
                />
                <Select size={"small"} value={maxDepth} onChange={(e) => setMaxDepth(e.target.value)} sx={{
                    width: 120,
                    "& .MuiOutlinedInput-notchedOutline": {borderColor: "divider"},
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {borderColor: "divider", borderWidth: "1px"},
                }}>
                    <MenuItem value={"1"}>Depth 1</MenuItem>
                    <MenuItem value={"2"}>Depth 2</MenuItem>
                    <MenuItem value={"3"}>Depth 3</MenuItem>
                </Select>
                <Select size={"small"} value={maxNodes} onChange={(e) => setMaxNodes(e.target.value)}
                        sx={{
                            width: 130,
                            "& .MuiOutlinedInput-notchedOutline": {borderColor: "divider"},
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "divider",
                                borderWidth: "1px"
                            },
                        }}>
                    <MenuItem value={"50"}>50 nodes</MenuItem>
                    <MenuItem value={"100"}>100 nodes</MenuItem>
                    <MenuItem value={"200"}>200 nodes</MenuItem>
                </Select>
                <Button
                    onClick={() => void loadGraph(labelQuery)}
                    sx={{
                        bgcolor: "#000",
                        color: "#fff",
                        textTransform: "none",
                        borderRadius: 2,
                        boxShadow: "none",
                        "&:hover": {bgcolor: "#000"}
                    }}
                >
                    Load Graph
                </Button>
            </Stack>

            <Stack direction={"row"} gap={1} flexWrap={"wrap"} alignItems={"center"}>
                {activeLabel && (
                    <Chip label={`Active: ${activeLabel}`} variant={"outlined"} size={"small"} sx={{fontSize: 11}}/>
                )}
                {labelSuggestions.slice(0, 8).map(label => (
                    <Button
                        key={label}
                        size={"small"}
                        onClick={() => {
                            setLabelQuery(label);
                            void loadGraph(label);
                        }}
                        sx={label === activeLabel
                            ? {
                                bgcolor: "#000",
                                color: "#fff",
                                textTransform: "none",
                                fontSize: 11,
                                borderRadius: 2,
                                boxShadow: "none",
                                "&:hover": {bgcolor: "#000"}
                            }
                            : {
                                textTransform: "none",
                                fontSize: 11,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                color: "text.primary"
                            }}
                    >
                        {label}
                    </Button>
                ))}
            </Stack>

            <Stack direction={"row"} gap={1.25} flexWrap={"wrap"} alignItems={"center"}>
                <TextField
                    size={"small"}
                    sx={{
                        flex: 1,
                        minWidth: 200,
                        "& .MuiOutlinedInput-root": {
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "divider",
                                borderWidth: "1px",
                            },
                        },
                    }}
                    value={nodeSearchTerm}
                    onChange={(e) => setNodeSearchTerm(e.target.value)}
                    placeholder={"Filter visible nodes..."}
                    InputProps={{
                        startAdornment: <InputAdornment position={"start"}><IconSearch size={14}/></InputAdornment>
                    }}
                />
                <Select
                    size={"small"} value={filterType} onChange={(e) => setFilterType(e.target.value)}
                    sx={{
                        width: 140,
                        "& .MuiOutlinedInput-notchedOutline": {borderColor: "divider"},
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {borderColor: "divider", borderWidth: "1px"},
                    }}
                    renderValue={(v) => (
                        <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                            <IconFilter size={13}/> {v === "all" ? "All Types" : v}
                        </Stack>
                    )}
                >
                    <MenuItem value={"all"}>All Types</MenuItem>
                    <MenuItem value={"entity"}>Entities</MenuItem>
                    <MenuItem value={"relation"}>Relations</MenuItem>
                    <MenuItem value={"chunk"}>Chunks</MenuItem>
                    <MenuItem value={"topic"}>Topic</MenuItem>
                </Select>
                <Stack direction={"row"} gap={0.5}>
                    <IconButton
                        size={"small"} sx={{border: "1px solid", borderColor: "divider", borderRadius: 1.5}}
                        onClick={() => setZoom(z => Math.min(3, z * 1.2))}
                    >
                        <IconZoomIn size={16}/>
                    </IconButton>
                    <IconButton
                        size={"small"} sx={{border: "1px solid", borderColor: "divider", borderRadius: 1.5}}
                        onClick={() => setZoom(z => Math.max(0.2, z * 0.8))}
                    >
                        <IconZoomOut size={16}/>
                    </IconButton>
                    <IconButton
                        size={"small"} sx={{border: "1px solid", borderColor: "divider", borderRadius: 1.5}}
                        onClick={resetView}
                    >
                        <IconRotateClockwise2 size={16}/>
                    </IconButton>
                </Stack>
            </Stack>

            {error && (
                <Card sx={{...cardSx, borderColor: "error.light", bgcolor: "#fef2f2"}}>
                    <CardContent sx={{py: 1.5}}>
                        <Typography fontSize={12} color={"error.main"}>{error}</Typography>
                    </CardContent>
                </Card>
            )}

            <Card sx={{...cardSx, overflow: "hidden"}}>
                <Box sx={{position: "relative"}}>
                    {isLoading && (
                        <Box sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "rgba(255,255,255,0.8)",
                            zIndex: 10,
                        }}>
                            <Box sx={{textAlign: "center"}}>
                                <Box sx={{
                                    width: 24, height: 24, border: "2px solid", borderColor: "#000",
                                    borderTopColor: "transparent", borderRadius: "50%", mx: "auto", mb: 1,
                                    animation: "spin 0.8s linear infinite",
                                    "@keyframes spin": {to: {transform: "rotate(360deg)"}},
                                }}/>
                                <Typography fontSize={12} color={"text.secondary"}>Fetching knowledge
                                    graph...</Typography>
                            </Box>
                        </Box>
                    )}
                    <Box
                        component={"canvas"}
                        ref={canvasRef}
                        sx={{
                            width: "100%", height: 450,
                            background: "linear-gradient(to bottom right, #fafafa, #fff)",
                        }}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        onDoubleClick={handleDoubleClick}
                        onWheel={handleWheel}
                    />

                    <Box sx={{
                        position: "absolute",
                        bottom: 12,
                        left: 12,
                        bgcolor: "rgba(255,255,255,0.9)",
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        p: 1,
                        fontSize: 10,
                    }}>
                        <Typography fontSize={10} color={"text.secondary"}
                                    sx={{pb: 0.5, mb: 0.5, borderBottom: "1px solid", borderColor: "divider"}}>
                            Double-click a node to refocus
                        </Typography>
                        <Stack gap={0.5}>
                            <Stack direction={"row"} alignItems={"center"} gap={0.75}>
                                <Box sx={{width: 10, height: 10, borderRadius: "50%", bgcolor: topic.color}}/>
                                <Typography fontSize={10}>Focus Label</Typography>
                            </Stack>
                            <Stack direction={"row"} alignItems={"center"} gap={0.75}>
                                <Box sx={{width: 10, height: 10, borderRadius: "50%", bgcolor: "#6366f1"}}/>
                                <Typography fontSize={10}>Entity</Typography>
                            </Stack>
                            <Stack direction={"row"} alignItems={"center"} gap={0.75}>
                                <Box sx={{width: 10, height: 10, borderRadius: "50%", bgcolor: "#f97316"}}/>
                                <Typography fontSize={10}>Relation</Typography>
                            </Stack>
                            <Stack direction={"row"} alignItems={"center"} gap={0.75}>
                                <Box sx={{width: 10, height: 10, borderRadius: "50%", bgcolor: "#22c55e"}}/>
                                <Typography fontSize={10}>Chunk</Typography>
                            </Stack>
                        </Stack>
                    </Box>

                    <Box sx={{
                        position: "absolute",
                        bottom: 12,
                        right: 12,
                        bgcolor: "rgba(255,255,255,0.9)",
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        px: 1,
                        py: 0.5,
                    }}>
                        <Typography fontSize={10} color={"text.secondary"}>{Math.round(zoom * 100)}%</Typography>
                    </Box>
                </Box>
            </Card>

            {selectedNode && (
                <Card sx={cardSx}>
                    <CardContent sx={{pt: 2, pb: 1.5}}>
                        <Stack direction={"row"} alignItems={"center"} gap={1} sx={{mb: 1}}>
                            <Box sx={{width: 12, height: 12, borderRadius: "50%", bgcolor: selectedNode.color}}/>
                            <Typography fontSize={14}>{selectedNode.label}</Typography>
                            <Chip label={selectedNode.type} variant={"outlined"} size={"small"} sx={{fontSize: 10}}/>
                        </Stack>
                        <Stack gap={0.5} sx={{fontSize: 12, color: "text.secondary"}}>
                            <Typography fontSize={12} color={"text.secondary"}>
                                Connections: {graph.edges.filter(e => e.source === selectedNode.id || e.target === selectedNode.id).length}
                            </Typography>
                            <Typography fontSize={12} color={"text.secondary"}>
                                Connected to: {graph.edges
                                .filter(e => e.source === selectedNode.id || e.target === selectedNode.id)
                                .map(e => {
                                    const otherId = e.source === selectedNode.id ? e.target : e.source;
                                    return graph.nodes.find(n => n.id === otherId)?.label;
                                })
                                .filter(Boolean)
                                .join(", ")}
                            </Typography>
                        </Stack>
                        {selectedNode.properties && Object.keys(selectedNode.properties).length > 0 && (
                            <Box sx={{mt: 1.5, pt: 1.5, borderTop: "1px solid", borderColor: "divider"}}>
                                <Typography fontSize={12} sx={{mb: 1}}>Attributes</Typography>
                                <Stack gap={1}>
                                    {Object.entries(selectedNode.properties).map(([key, value]) => (
                                        <Stack key={key} direction={"row"} gap={1.5}>
                                            <Typography fontSize={12} color={"text.secondary"}
                                                        sx={{width: 120, wordBreak: "break-all"}}>
                                                {key}
                                            </Typography>
                                            <Typography fontSize={12}
                                                        sx={{wordBreak: "break-word"}}>{String(value)}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}

            <Stack direction={"row"} gap={1.5}>
                {[
                    {value: graph.nodes.length, label: "Nodes"},
                    {value: graph.edges.length, label: "Edges"},
                    {value: popularLabels.length, label: "Popular Labels"},
                ].map(stat => (
                    <Box key={stat.label}
                         sx={{flex: 1, bgcolor: "action.hover", borderRadius: 1.5, p: 1.5, textAlign: "center"}}>
                        <Typography fontSize={20}>{stat.value}</Typography>
                        <Typography fontSize={11} color={"text.secondary"}>{stat.label}</Typography>
                    </Box>
                ))}
            </Stack>

        </Stack>
    );
};